import React, { useState } from 'react';
import {
  Hand, Pencil, Highlighter, Eraser,
  Square, Circle, Minus, ArrowRight,
  Type, MapPin, EyeOff, Layers,
  FlipHorizontal, FlipVertical,
  Undo2, Redo2, Eye, EyeOff as EyeOffIcon, RotateCcw,
  ZoomIn, ZoomOut, Maximize, Move,
} from 'lucide-react';
import type { EditorTool, PinStyle } from './types';
import { PRESET_COLORS } from './types';

interface ToolButtonProps {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

const ToolButton: React.FC<ToolButtonProps> = ({ icon, label, active, disabled, onClick }) =>
  (
    <button
      className={`ife-tool-btn ${active ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
      title={label}
      onClick={onClick}
      disabled={disabled}
      type="button"
    >
      {icon}
    </button>
  );

interface ToolGroupProps {
  label: string;
  children: React.ReactNode;
}

const ToolGroup: React.FC<ToolGroupProps> = ({ label, children }) =>
  (
    <div className="ife-tool-group">
      <span className="ife-tool-group-label">{label}</span>
      <div className="ife-tool-group-buttons">
        {children}
      </div>
    </div>
  );

interface ImageEditorToolbarProps {
  tool: EditorTool;
  setTool: (t: EditorTool) => void;

  color: string;
  setColor: (c: string) => void;
  lineWidth: number;
  setLineWidth: (w: number) => void;
  opacity: number;
  setOpacity: (o: number) => void;
  fillShape: boolean;
  setFillShape: (f: boolean) => void;
  fontSize: number;
  setFontSize: (s: number) => void;
  textBold: boolean;
  setTextBold: (b: boolean) => void;
  textItalic: boolean;
  setTextItalic: (i: boolean) => void;
  textHasBackground: boolean;
  setTextHasBackground: (h: boolean) => void;
  textBackgroundColor: string;
  setTextBackgroundColor: (c: string) => void;

  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
  reset: () => void;
  showAnnotations: boolean;
  toggleAnnotations: () => void;
  flipHorizontal: () => void;
  flipVertical: () => void;

  zoom: number;
  zoomIn: () => void;
  zoomOut: () => void;
  zoomReset: () => void;
  fitToScreen: () => void;

  pinStyle: PinStyle;
  pinValue: string;
  setPinStyle: (style: PinStyle) => void;
  setPinValue: (value: string) => void;
  resetPinValue: () => void;
}

export const ImageEditorToolbar: React.FC<ImageEditorToolbarProps> = ({
  tool, setTool,
  color, setColor,
  lineWidth, setLineWidth,
  opacity, setOpacity,
  fillShape, setFillShape,
  fontSize, setFontSize,
  textBold, setTextBold,
  textItalic, setTextItalic,
  textHasBackground, setTextHasBackground,
  textBackgroundColor, setTextBackgroundColor,
  canUndo, canRedo, undo, redo, reset,
  showAnnotations, toggleAnnotations,
  flipHorizontal, flipVertical,
  zoom, zoomIn, zoomOut, fitToScreen,
  pinStyle, pinValue, setPinStyle, setPinValue, resetPinValue,
}) =>
{
  const [hexInput, setHexInput] = useState(color);
  const handleHexChange = (e: React.ChangeEvent<HTMLInputElement>) =>
  {
    const val = e.target.value;
    setHexInput(val);
    if (/^#[0-9A-Fa-f]{6}$/.test(val)) setColor(val);
  };

  const handleColorSelect = (c: string) =>
  {
    setColor(c);
    setHexInput(c);
  };

  const handleReset = () =>
  {
    reset();
  };

  const drawingTools: Array<{ id: EditorTool; icon: React.ReactNode; label: string }> = [
    { id: 'hand', icon: <Hand size={16} />, label: 'Hand (Pan)' },
    { id: 'move', icon: <Move size={16} />, label: 'Move/Select' },
    { id: 'pencil', icon: <Pencil size={16} />, label: 'Pencil' },
    { id: 'highlighter', icon: <Highlighter size={16} />, label: 'Highlighter' },
    { id: 'eraser', icon: <Eraser size={16} />, label: 'Eraser' },
  ];

  const shapeTools: Array<{ id: EditorTool; icon: React.ReactNode; label: string }> = [
    { id: 'rect', icon: <Square size={16} />, label: 'Rectangle' },
    { id: 'circle', icon: <Circle size={16} />, label: 'Circle/Ellipse' },
    { id: 'line', icon: <Minus size={16} />, label: 'Line' },
    { id: 'arrow', icon: <ArrowRight size={16} />, label: 'Arrow' },
  ];

  const annotTools: Array<{ id: EditorTool; icon: React.ReactNode; label: string }> = [
    { id: 'text', icon: <Type size={16} />, label: 'Text' },
    { id: 'pin', icon: <MapPin size={16} />, label: 'Numbered Pin' },
  ];

  const privacyTools: Array<{ id: EditorTool; icon: React.ReactNode; label: string }> = [
    { id: 'blur', icon: <Layers size={16} />, label: 'Blur / Pixelate' },
    { id: 'censor', icon: <EyeOff size={16} />, label: 'Censor (opaque block)' },
  ];

  const showFillToggle = ['rect', 'circle'].includes(tool);
  const showTextOptions = tool === 'text';
  const showPinOptions = tool === 'pin';

  return (
    <div className="ife-toolbar">
      {/* History + View */}
      <div className="ife-toolbar-top">
        <ToolButton
          icon={<Undo2 size={15} />}
          label="Undo (Ctrl+Z)"
          disabled={!canUndo}
          onClick={undo}
        />
        <ToolButton
          icon={<Redo2 size={15} />}
          label="Redo (Ctrl+Shift+Z)"
          disabled={!canRedo}
          onClick={redo}
        />
        <ToolButton
          icon={showAnnotations ? <Eye size={15} /> : <EyeOffIcon size={15} />}
          label={showAnnotations ? 'Hide annotations' : 'Show annotations'}
          active={!showAnnotations}
          onClick={toggleAnnotations}
        />
        <ToolButton
          icon={<RotateCcw size={15} />}
          label="Reset all annotations"
          onClick={handleReset}
        />
      </div>

      <div className="ife-toolbar-divider" />

      {/* Zoom controls */}
      <div className="ife-toolbar-top">
        <ToolButton
          icon={<ZoomOut size={15} />}
          label="Zoom Out"
          disabled={zoom <= 0.25}
          onClick={zoomOut}
        />
        <span style={{ fontSize: 11, color: 'var(--ife-text-muted)', padding: '0 4px', minWidth: 45, textAlign: 'center' }}>
          {Math.round(zoom * 100)}%
        </span>
        <ToolButton
          icon={<ZoomIn size={15} />}
          label="Zoom In"
          disabled={zoom >= 3}
          onClick={zoomIn}
        />
        <ToolButton
          icon={<Maximize size={15} />}
          label="Fit to Screen"
          onClick={fitToScreen}
        />
      </div>

      <div className="ife-toolbar-divider" />

      {/* Drawing tools */}
      <ToolGroup label="Draw">
        {drawingTools.map(t => (
          <ToolButton
            key={t.id}
            icon={t.icon}
            label={t.label}
            active={tool === t.id}
            onClick={() => setTool(t.id)}
          />
        ))}
      </ToolGroup>

      {/* Shape tools */}
      <ToolGroup label="Shapes">
        {shapeTools.map(t => (
          <ToolButton
            key={t.id}
            icon={t.icon}
            label={t.label}
            active={tool === t.id}
            onClick={() => setTool(t.id)}
          />
        ))}
      </ToolGroup>

      {/* Annotation tools */}
      <ToolGroup label="Annotate">
        {annotTools.map(t => (
          <ToolButton
            key={t.id}
            icon={t.icon}
            label={t.label}
            active={tool === t.id}
            onClick={() => setTool(t.id)}
          />
        ))}
      </ToolGroup>

      {/* Privacy tools */}
      <ToolGroup label="Privacy">
        {privacyTools.map(t => (
          <ToolButton
            key={t.id}
            icon={t.icon}
            label={t.label}
            active={tool === t.id}
            onClick={() => setTool(t.id)}
          />
        ))}
      </ToolGroup>

      {/* Transform */}
      <ToolGroup label="Transform">
        <ToolButton
          icon={<FlipHorizontal size={16} />}
          label="Flip Horizontal"
          onClick={flipHorizontal}
        />
        <ToolButton
          icon={<FlipVertical size={16} />}
          label="Flip Vertical"
          onClick={flipVertical}
        />
      </ToolGroup>

      <div className="ife-toolbar-divider" />

      {/* Tool properties */}
      <div className="ife-tool-props">
        {/* Color palette */}
        <div className="ife-prop-section">
          <span className="ife-prop-label">Color</span>
          <div className="ife-color-palette">
            {PRESET_COLORS.map(c => (
              <button
                key={c}
                type="button"
                className={`ife-color-swatch ${color === c ? 'active' : ''}`}
                style={{ backgroundColor: c }}
                title={c}
                onClick={() => handleColorSelect(c)}
              />
            ))}
          </div>
          <input
            className="ife-hex-input"
            type="text"
            value={hexInput}
            onChange={handleHexChange}
            placeholder="#000000"
            maxLength={7}
          />
        </div>

        {/* Line width */}
        {tool !== 'text' && tool !== 'pin' && (
          <div className="ife-prop-section">
            <span className="ife-prop-label">Width: {lineWidth}px</span>
            <input
              type="range"
              min={1}
              max={20}
              value={lineWidth}
              onChange={e => setLineWidth(Number(e.target.value))}
              className="ife-slider"
            />
            <div className="ife-width-presets">
              {[1, 3, 6, 10].map(w => (
                <button
                  key={w}
                  type="button"
                  className={`ife-preset-btn ${lineWidth === w ? 'active' : ''}`}
                  onClick={() => setLineWidth(w)}
                >
                  {w}px
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Opacity */}
        <div className="ife-prop-section">
          <span className="ife-prop-label">Opacity: {Math.round(opacity * 100)}%</span>
          <input
            type="range"
            min={10}
            max={100}
            value={Math.round(opacity * 100)}
            onChange={e => setOpacity(Number(e.target.value) / 100)}
            className="ife-slider"
          />
        </div>

        {/* Fill toggle for shapes */}
        {showFillToggle && (
          <div className="ife-prop-section">
            <label className="ife-toggle-label">
              <input
                type="checkbox"
                checked={fillShape}
                onChange={e => setFillShape(e.target.checked)}
              />
              <span>Fill shape</span>
            </label>
          </div>
        )}

        {/* Text options */}
        {showTextOptions && (
          <>
            <div className="ife-prop-section">
              <span className="ife-prop-label">Font size: {fontSize}px</span>
              <input
                type="range"
                min={10}
                max={72}
                value={fontSize}
                onChange={e => setFontSize(Number(e.target.value))}
                className="ife-slider"
              />
              <div className="ife-text-style-btns">
                <button
                  type="button"
                  className={`ife-style-btn ${textBold ? 'active' : ''}`}
                  onClick={() => setTextBold(!textBold)}
                >
                  <strong>B</strong>
                </button>
                <button
                  type="button"
                  className={`ife-style-btn ${textItalic ? 'active' : ''}`}
                  onClick={() => setTextItalic(!textItalic)}
                >
                  <em>I</em>
                </button>
              </div>
            </div>
            <div className="ife-prop-section">
              <label className="ife-toggle-label">
                <input
                  type="checkbox"
                  checked={textHasBackground}
                  onChange={e => setTextHasBackground(e.target.checked)}
                />
                <span>Text background</span>
              </label>
              {textHasBackground && (
                <>
                  <span className="ife-prop-label" style={{ marginTop: 8 }}>Background color</span>
                  <input
                    className="ife-hex-input"
                    type="text"
                    value={textBackgroundColor}
                    onChange={e =>
                    {
                      const val = e.target.value;
                      if (/^#[0-9A-Fa-f]{0,6}$/.test(val))
                      {
                        setTextBackgroundColor(val);
                      }
                    }}
                    placeholder="#1e1e2e"
                    maxLength={7}
                    style={{ marginTop: 4 }}
                  />
                </>
              )}
            </div>
          </>
        )}

        {/* Pin options */}
        {showPinOptions && (
          <div className="ife-prop-section">
            <span className="ife-prop-label">Pin Style</span>
            <select
              className="ife-select"
              value={pinStyle}
              onChange={e =>
              {
                const newStyle = e.target.value as PinStyle;
                setPinStyle(newStyle);
              }}
            >
              <option value="numbers">Numbers (1,2,3)</option>
              <option value="letters-upper">Letters (A,B,C)</option>
              <option value="letters-lower">Letters (a,b,c)</option>
            </select>
            <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
              <div style={{ flex: 1 }}>
                <span className="ife-prop-label">Next Pin: {pinValue}</span>
                <input
                  type="text"
                  className="ife-text-input-small"
                  value={pinValue}
                  onChange={e =>
                  {
                    setPinValue(e.target.value);
                  }}
                  style={{ width: '100%', marginTop: 4 }}
                />
              </div>
              <button
                type="button"
                className="ife-tool-btn"
                title="Reset to start"
                onClick={e =>
                {
                  e.preventDefault();
                  e.stopPropagation();
                  resetPinValue();
                }}
                style={{ marginTop: 18 }}
              >
                <RotateCcw size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageEditorToolbar;
